import { useEffect, useState, type FormEvent } from "react";
import { ApiError } from "@/lib/api";
import { Button, Dialog, Field, Input, Select } from "@/components/ui/primitives";
import { useCustomers } from "@/features/customers/hooks";
import { useCreateContract, useUpdateContract } from "./hooks";
import { CONTRACT_TYPE_LABELS } from "./labels";
import type { ContractDetail, ContractListItem, ContractType } from "./api";

interface Props {
  open: boolean;
  onClose: () => void;
  editing: ContractListItem | ContractDetail | null;
}

const EMPTY = {
  customer_id: "",
  contract_number: "",
  provider: "",
  contract_type: "amc" as ContractType,
  start_date: "",
  end_date: "",
  cost: "",
  is_active: true,
  notes: "",
};

export function ContractFormDialog({ open, onClose, editing }: Props) {
  const create = useCreateContract();
  const update = useUpdateContract();
  const [form, setForm] = useState({ ...EMPTY });
  const [error, setError] = useState<string | null>(null);

  const { data: customers } = useCustomers({ page_size: 100 }, { enabled: open && !editing });

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        customer_id: editing.customer_id,
        contract_number: editing.contract_number,
        provider: editing.provider ?? "",
        contract_type: editing.contract_type,
        start_date: editing.start_date,
        end_date: editing.end_date,
        cost: editing.cost ?? "",
        is_active: editing.is_active,
        notes: "",
      });
    } else {
      setForm({ ...EMPTY });
    }
    setError(null);
  }, [open, editing]);

  const set = <K extends keyof typeof EMPTY>(k: K) => (v: (typeof EMPTY)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const body = {
      contract_number: form.contract_number.trim(),
      provider: form.provider.trim() || null,
      contract_type: form.contract_type,
      start_date: form.start_date,
      end_date: form.end_date,
      cost: form.cost.trim() || null,
      is_active: form.is_active,
      notes: form.notes.trim() || null,
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, body });
      } else {
        await create.mutateAsync({ customer_id: form.customer_id, ...body });
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the contract.");
    }
  }

  const canSubmit =
    form.contract_number.trim() !== "" &&
    form.start_date !== "" &&
    form.end_date !== "" &&
    (editing !== null || form.customer_id !== "");

  return (
    <Dialog open={open} onClose={onClose} title={editing ? "Edit contract" : "Add contract"}>
      <form onSubmit={submit} className="space-y-3">
        {error && (
          <div role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        {!editing && (
          <Field label="Customer" htmlFor="c_customer">
            <Select
              id="c_customer"
              value={form.customer_id}
              onChange={(e) => set("customer_id")(e.target.value)}
            >
              <option value="">Select a customer…</option>
              {(customers?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company_name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Contract number" htmlFor="c_number">
            <Input
              id="c_number"
              value={form.contract_number}
              onChange={(e) => set("contract_number")(e.target.value)}
              placeholder="e.g. AMC-2026-001"
            />
          </Field>
          <Field label="Type" htmlFor="c_type">
            <Select
              id="c_type"
              value={form.contract_type}
              onChange={(e) => set("contract_type")(e.target.value as ContractType)}
            >
              {Object.entries(CONTRACT_TYPE_LABELS).map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Provider / vendor" htmlFor="c_provider">
          <Input id="c_provider" value={form.provider} onChange={(e) => set("provider")(e.target.value)} />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Start date" htmlFor="c_start">
            <Input id="c_start" type="date" value={form.start_date} onChange={(e) => set("start_date")(e.target.value)} />
          </Field>
          <Field label="End date" htmlFor="c_end">
            <Input id="c_end" type="date" value={form.end_date} onChange={(e) => set("end_date")(e.target.value)} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Cost (₹)" htmlFor="c_cost">
            <Input
              id="c_cost"
              type="number"
              min="0"
              step="0.01"
              value={form.cost}
              onChange={(e) => set("cost")(e.target.value)}
            />
          </Field>
          {editing && (
            <Field label="Active" htmlFor="c_active">
              <Select
                id="c_active"
                value={form.is_active ? "yes" : "no"}
                onChange={(e) => set("is_active")(e.target.value === "yes")}
              >
                <option value="yes">Active</option>
                <option value="no">Cancelled</option>
              </Select>
            </Field>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit || create.isPending || update.isPending}>
            {editing ? "Save changes" : "Create contract"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
